import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Get zodiac sign and element from birth date
 */
const getZodiacInfo = (birthDate) => {
    if (!birthDate) return { sign: 'Aries', element: 'Fire', symbol: '♈' };
    
    const date = new Date(birthDate);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    
    const zodiacData = [
        { sign: 'Capricorn', element: 'Earth', symbol: '♑', start: [1, 1], end: [1, 19] },
        { sign: 'Aquarius', element: 'Air', symbol: '♒', start: [1, 20], end: [2, 18] },
        { sign: 'Pisces', element: 'Water', symbol: '♓', start: [2, 19], end: [3, 20] },
        { sign: 'Aries', element: 'Fire', symbol: '♈', start: [3, 21], end: [4, 19] },
        { sign: 'Taurus', element: 'Earth', symbol: '♉', start: [4, 20], end: [5, 20] },
        { sign: 'Gemini', element: 'Air', symbol: '♊', start: [5, 21], end: [6, 20] },
        { sign: 'Cancer', element: 'Water', symbol: '♋', start: [6, 21], end: [7, 22] },
        { sign: 'Leo', element: 'Fire', symbol: '♌', start: [7, 23], end: [8, 22] },
        { sign: 'Virgo', element: 'Earth', symbol: '♍', start: [8, 23], end: [9, 22] },
        { sign: 'Libra', element: 'Air', symbol: '♎', start: [9, 23], end: [10, 22] },
        { sign: 'Scorpio', element: 'Water', symbol: '♏', start: [10, 23], end: [11, 21] },
        { sign: 'Sagittarius', element: 'Fire', symbol: '♐', start: [11, 22], end: [12, 21] },
        { sign: 'Capricorn', element: 'Earth', symbol: '♑', start: [12, 22], end: [12, 31] },
    ];
    
    for (const z of zodiacData) {
        const [startMonth, startDay] = z.start;
        const [endMonth, endDay] = z.end;
        
        if ((month === startMonth && day >= startDay) || (month === endMonth && day <= endDay)) {
            return { sign: z.sign, element: z.element, symbol: z.symbol };
        }
    }
    
    return { sign: 'Aries', element: 'Fire', symbol: '♈' };
};

/**
 * Element-based vocabulary for prophecy generation
 */
const elementVocabulary = {
    Fire: {
        verbs: ['burns', 'blazes', 'ignites', 'consumes', 'radiates'],
        nouns: ['flame', 'inferno', 'ember', 'spark', 'phoenix'],
        adjectives: ['fierce', 'passionate', 'bold', 'radiant', 'untamed'],
        forces: ['solar intensity', 'volcanic power', 'starfire', 'divine fury']
    },
    Earth: {
        verbs: ['grounds', 'nurtures', 'endures', 'manifests', 'roots'],
        nouns: ['mountain', 'crystal', 'foundation', 'garden', 'stone'],
        adjectives: ['steadfast', 'patient', 'abundant', 'ancient', 'fertile'],
        forces: ['primordial strength', 'earthen wisdom', 'deep roots', 'silent power']
    },
    Air: {
        verbs: ['whispers', 'carries', 'transforms', 'liberates', 'connects'],
        nouns: ['wind', 'breath', 'storm', 'thought', 'messenger'],
        adjectives: ['swift', 'curious', 'boundless', 'ethereal', 'brilliant'],
        forces: ['celestial currents', 'mental clarity', 'cosmic whispers', 'infinite possibility']
    },
    Water: {
        verbs: ['flows', 'dreams', 'heals', 'dissolves', 'reflects'],
        nouns: ['ocean', 'tide', 'current', 'depths', 'moon-pool'],
        adjectives: ['deep', 'mysterious', 'intuitive', 'fluid', 'sacred'],
        forces: ['lunar tides', 'emotional depths', 'psychic currents', 'ancestral waters']
    }
};

/**
 * Palm line interpretations crossed with zodiac elements
 */
const palmSynthesis = {
    heartLine: {
        Fire: [
            "your heart burns with solar intensity; you lead with emotion and inspire others to feel deeply.",
            "passion courses through you like wildfire; your love transforms everything it touches.",
            "your emotional flame never dims; you are destined to ignite hearts wherever you wander."
        ],
        Earth: [
            "your heart beats with the patience of mountains; your love is a sanctuary that endures.",
            "you love with the constancy of stone; your devotion becomes the foundation others build upon.",
            "your emotional roots run deep into ancient soil; you carry the wisdom of timeless love."
        ],
        Air: [
            "your heart speaks in whispers that carry across worlds; connection is your sacred gift.",
            "you love with the freedom of wind; your heart refuses to be caged yet always returns.",
            "your emotions dance like thoughts in motion; you teach others new ways to feel."
        ],
        Water: [
            "you feel with the depth of an ocean; your passion is a secret current that moves all things.",
            "your heart holds the mystery of tides; your love ebbs and flows but never truly retreats.",
            "emotional intuition is your birthright; you sense what others cannot speak."
        ]
    },
    headLine: {
        Fire: [
            "your mind blazes with creative fire; thoughts become actions before others can react.",
            "intellect and instinct merge in you like flame and fuel; you think with your whole being."
        ],
        Earth: [
            "your thoughts are rooted in practical wisdom; you build ideas into reality.",
            "mental endurance is your gift; where others falter, your mind remains steadfast."
        ],
        Air: [
            "your mind moves like lightning across vast skies; no idea is too distant to grasp.",
            "thoughts flow through you like infinite currents; you are a vessel of cosmic intelligence."
        ],
        Water: [
            "your mind swims in intuitive depths; you understand what logic alone cannot teach.",
            "dreams and thoughts merge in your consciousness; you think in poetry and prophecy."
        ]
    },
    lifeLine: {
        Fire: [
            "your life force burns bright and fierce; you are meant to live boldly and leave ashes behind.",
            "vitality radiates from your core; you carry enough fire for multiple lifetimes."
        ],
        Earth: [
            "your life is built on enduring foundations; you will outlast storms others cannot weather.",
            "the earth itself feeds your vitality; nature is your constant ally."
        ],
        Air: [
            "your life path weaves through many realms; change is not your enemy but your companion.",
            "freedom defines your journey; you must breathe new air to truly live."
        ],
        Water: [
            "your life flows like a sacred river; every bend reveals new depths of experience.",
            "healing energy moves through your existence; you transform pain into wisdom."
        ]
    }
};

/**
 * Generate a unique prophecy based on zodiac and simulated palm reading
 */
const generateProphecy = (zodiacInfo, hasPalmImage) => {
    const element = zodiacInfo.element;
    const vocab = elementVocabulary[element];
    
    // Select random palm aspects to read
    const palmAspects = ['heartLine', 'headLine', 'lifeLine'];
    const selectedAspect = palmAspects[Math.floor(Math.random() * palmAspects.length)];
    const readings = palmSynthesis[selectedAspect][element];
    const mainReading = readings[Math.floor(Math.random() * readings.length)];
    
    // Generate dynamic closing based on element
    const verb = vocab.verbs[Math.floor(Math.random() * vocab.verbs.length)];
    const force = vocab.forces[Math.floor(Math.random() * vocab.forces.length)];
    const adjective = vocab.adjectives[Math.floor(Math.random() * vocab.adjectives.length)];
    
    const closings = [
        `the ${force} within you ${verb} toward destiny.`,
        `your ${adjective} spirit awaits its true calling.`,
        `what ${verb} within you cannot be contained.`,
        `the stars have marked you ${adjective} among mortals.`
    ];
    
    const closing = closings[Math.floor(Math.random() * closings.length)];
    
    return hasPalmImage 
        ? mainReading
        : `even without your palm, the ${zodiacInfo.sign} in you speaks clearly. ${mainReading}`;
};

const mysticalPhrases = [
    "aligning your celestial houses...",
    "tracing the lines of destiny...",
    "consulting the ancient codex...",
    "merging your stars with your skin...",
    "channeling your element's wisdom..."
];

export const CalibrationScreen = ({ palmImage, userData, onComplete }) => {
    const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
    const [constellationPoints, setConstellationPoints] = useState([]);
    const [showProphecy, setShowProphecy] = useState(false);
    const [prophecy, setProphecy] = useState("");
    
    // Calculate zodiac info from birth date
    const zodiacInfo = useMemo(() => {
        return getZodiacInfo(userData?.birthDate);
    }, [userData?.birthDate]);
    
    // Generate unique prophecy
    useEffect(() => {
        const generatedProphecy = generateProphecy(zodiacInfo, !!palmImage);
        setProphecy(generatedProphecy);
    }, [zodiacInfo, palmImage]);

    useEffect(() => {
        // Generate constellation points
        const points = [];
        for (let i = 0; i < 25; i++) {
            points.push({
                id: i,
                x: 20 + Math.random() * 60,
                y: 20 + Math.random() * 60,
                delay: i * 0.15
            });
        }
        setConstellationPoints(points);

        // Cycle through phrases
        const phraseInterval = setInterval(() => {
            setCurrentPhraseIndex(prev => {
                if (prev < mysticalPhrases.length - 1) {
                    return prev + 1;
                }
                return prev;
            });
        }, 1000);
        
        // Show prophecy after phrases
        const prophecyTimeout = setTimeout(() => {
            setShowProphecy(true);
        }, 4500);

        // Complete after prophecy display
        const completeTimeout = setTimeout(() => {
            if (onComplete) onComplete();
        }, 8000);

        return () => {
            clearInterval(phraseInterval);
            clearTimeout(prophecyTimeout);
            clearTimeout(completeTimeout);
        };
    }, [onComplete]);

    // Generate constellation lines
    const generateLines = () => {
        const lines = [];
        for (let i = 0; i < constellationPoints.length - 1; i++) {
            if (Math.random() > 0.4) {
                const targetIndex = Math.min(i + 1 + Math.floor(Math.random() * 3), constellationPoints.length - 1);
                lines.push({
                    id: `line-${i}`,
                    x1: constellationPoints[i].x,
                    y1: constellationPoints[i].y,
                    x2: constellationPoints[targetIndex].x,
                    y2: constellationPoints[targetIndex].y,
                    delay: i * 0.1
                });
            }
        }
        return lines;
    };

    return (
        <motion.div
            className="fixed inset-0 flex flex-col items-center justify-center z-50 px-6"
            style={{
                background: "linear-gradient(180deg, hsl(240 10% 4%) 0%, hsl(255 15% 8%) 50%, hsl(240 10% 4%) 100%)"
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
        >
            {/* Background palm image */}
            <motion.div
                className="absolute inset-0 flex items-center justify-center opacity-10"
                initial={{ scale: 1.2, opacity: 0 }}
                animate={{ scale: 1, opacity: 0.1 }}
                transition={{ duration: 1.5 }}
            >
                <div 
                    className="w-80 h-80 rounded-full"
                    style={{
                        background: palmImage ? `url(${palmImage})` : "radial-gradient(circle, hsl(43 66% 52% / 0.2), transparent)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        filter: "blur(2px)"
                    }}
                />
            </motion.div>

            {/* Zodiac symbol display */}
            <motion.div
                className="absolute top-20 flex flex-col items-center"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
            >
                <span className="text-4xl text-primary/60">{zodiacInfo.symbol}</span>
                <span className="text-xs font-inter text-primary/40 tracking-widest mt-2">
                    {zodiacInfo.sign} · {zodiacInfo.element}
                </span>
            </motion.div>

            {/* Constellation mapping */}
            <div className="relative w-72 h-72 sm:w-80 sm:h-80">
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
                    {/* Constellation lines */}
                    {constellationPoints.length > 0 && generateLines().map((line) => (
                        <motion.line
                            key={line.id}
                            x1={line.x1}
                            y1={line.y1}
                            x2={line.x1}
                            y2={line.y1}
                            stroke="hsl(43, 66%, 52%)"
                            strokeWidth="0.3"
                            strokeOpacity="0.6"
                            initial={{ x2: line.x1, y2: line.y1 }}
                            animate={{ x2: line.x2, y2: line.y2 }}
                            transition={{ 
                                delay: line.delay + 0.5,
                                duration: 0.8,
                                ease: "easeOut"
                            }}
                        />
                    ))}
                    
                    {/* Constellation points */}
                    {constellationPoints.map((point) => (
                        <motion.circle
                            key={point.id}
                            cx={point.x}
                            cy={point.y}
                            r="0"
                            fill="hsl(43, 66%, 52%)"
                            initial={{ r: 0, opacity: 0 }}
                            animate={{ 
                                r: [0, 1.5, 1],
                                opacity: [0, 1, 0.8]
                            }}
                            transition={{
                                delay: point.delay,
                                duration: 0.6,
                                ease: "easeOut"
                            }}
                        />
                    ))}

                    {/* Central glow */}
                    <motion.circle
                        cx="50"
                        cy="50"
                        r="0"
                        fill="none"
                        stroke="hsl(43, 66%, 52%)"
                        strokeWidth="0.5"
                        initial={{ r: 0, opacity: 0 }}
                        animate={{ 
                            r: [0, 30, 25],
                            opacity: [0, 0.6, 0.3]
                        }}
                        transition={{
                            delay: 2,
                            duration: 1.5,
                            ease: "easeOut"
                        }}
                    />
                </svg>

                {/* Rotating outer ring */}
                <motion.div
                    className="absolute inset-0"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                        <circle
                            cx="50"
                            cy="50"
                            r="45"
                            fill="none"
                            stroke="hsl(43, 66%, 52%)"
                            strokeWidth="0.3"
                            strokeDasharray="2 4"
                            opacity="0.3"
                        />
                    </svg>
                </motion.div>
            </div>

            {/* Mystical phrases OR Prophecy */}
            <div className="h-32 flex items-center justify-center mt-6">
                <AnimatePresence mode="wait">
                    {!showProphecy ? (
                        <motion.p
                            key={`phrase-${currentPhraseIndex}`}
                            className="text-foreground font-inter text-sm sm:text-base tracking-wide text-center"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.4 }}
                        >
                            {mysticalPhrases[currentPhraseIndex]}
                        </motion.p>
                    ) : (
                        <motion.div
                            key="prophecy"
                            className="text-center max-w-md"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8 }}
                        >
                            <motion.p
                                className="text-primary/80 font-cinzel text-xs tracking-widest uppercase mb-3"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.3 }}
                            >
                                your prophecy
                            </motion.p>
                            <motion.p
                                className="text-foreground font-inter text-sm sm:text-base leading-relaxed italic"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5, duration: 0.6 }}
                            >
                                "{prophecy}"
                            </motion.p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Loading indicator */}
            {!showProphecy && (
                <motion.div
                    className="mt-6 flex gap-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                >
                    {[0, 1, 2].map((i) => (
                        <motion.div
                            key={i}
                            className="w-2 h-2 rounded-full bg-primary"
                            animate={{
                                scale: [1, 1.5, 1],
                                opacity: [0.3, 1, 0.3]
                            }}
                            transition={{
                                duration: 1.2,
                                repeat: Infinity,
                                delay: i * 0.2
                            }}
                        />
                    ))}
                </motion.div>
            )}
        </motion.div>
    );
};

export default CalibrationScreen;
